import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import * as appActions from "src/app/store/actions/app.actions";
import * as phoneActions from "src/app/store/actions/phone.actions";
import { Invitation, InvitationAcceptOptions, InviterOptions, MessagerOptions, RegistererRegisterOptions, Session, SessionInviteOptions, URI, UserAgentOptions } from "src/libs/SipJS";
import { SessionDescriptionHandler, SimpleUser, SimpleUserDelegate, SimpleUserMediaRemote, SimpleUserOptions, defaultSessionDescriptionHandlerFactory } from "src/libs/SipJS/platform/web";
import { Constants } from "../constants";
import { PartecipantRoles } from "../models/WebServer/partecipantRoles";
import { PartecipantStatus } from "../models/WebServer/partecipantStatus";
import { PhoneStatusEnum } from "../models/phoneStatus";
import { RootState } from "../store/root.state";
import { AppConfigService } from "./app.config.service";
import { FELoggerService, LogSource } from "./feLogger.service";
import { I18Service } from "./i18.service";
import { MediaStremService } from "./media-stream.service";
import { NotificationService } from "./notification.service";
import { StatsMonitoringService } from "./stats-monitoring.service";
import { VoiceActivityDetectorService } from "./voice-activity-detector.service";


@Injectable()
export class SipService {
  private sendingInvite = false;
  private inviteAttempts = 0;
  private ua: SimpleUser | undefined;
  private isRegistered = false;
  private stoppingAllSessions = false;
  private lastHangupTime = 0;

  constructor(
    private n: NotificationService,
    private statsService: StatsMonitoringService,
    private feLogger: FELoggerService,
    private mediaService: MediaStremService,
    private config: AppConfigService,
    private vadService: VoiceActivityDetectorService,
    private store: Store<RootState>) {
    console.debug(`SipSevice constructor`);
  }
  init() {
    // [PERF-LOAD-13] SIP Init Start
    const perfNow = performance.now();
    const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
    console.log(`[PERF-LOAD-13] SIP Init Start | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

    console.log(`sipService init`)
    const selfThis = this;

    if (this.ua) {
      FELoggerService.warn(`SipService already initialized - checking connection`);

      // Se il WebSocket è disconnesso (dopo maxAttempts), riconnetti prima di registrare
      if (!this.ua.isConnected()) {
        console.log('SIP WebSocket disconnected - reconnecting before register');

        // IMPORTANTE: Disconnect prima per cancellare eventuali timer di riconnessione automatica pendenti
        // Questo previene che i tentativi automatici continuino in background dopo la riconnessione manuale
        this.ua.disconnect()
          .then(() => {
            console.log('SIP disconnected - now reconnecting');
            return this.ua!.connect();
          })
          .then(() => {
            console.log('SIP WebSocket reconnected - now registering');
            return this.ua!.register();
          })
          .then(() => this.store.dispatch(appActions.wssSipRegistered({ is: true })))
          .catch((e) => {
            if (this.config.localDev) { //TODO HACK!! solo in localdev
              this.store.dispatch(appActions.wssSipRegistered({ is: true })); 
              return;
            }
            FELoggerService.error(`SIP reconnection failed`, LogSource.SIPEvent, e);
          });
      } else {
        // Già connesso, registra direttamente
      this.ua.register().then(() => this.store.dispatch(appActions.wssSipRegistered({ is: true })));
      }
      return;
    }
    const rtrProxy = this.config.rtrProxy;
    if (!rtrProxy) {
      FELoggerService.error(`SipService rtrProxy not defined`);
      return;
    }
    const aor = this.createAor(this.config.partecipantId);
    this.store.dispatch(appActions.loadingMessage({ message: I18Service.get("Loading.Connecting") }));
    const userAgentOptions: UserAgentOptions = {
      // uri: UserAgent.makeURI(`sip:${this.uaConfig.uri}`),
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: rtrProxy.iceGatheringTimeout,
        peerConnectionConfiguration: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:108.177.126.127:19302' }] },
      },
      sessionDescriptionHandlerFactory: defaultSessionDescriptionHandlerFactory(this.mediaService.localMediaStreamFactory()),
      transportOptions: {
        server: `${rtrProxy.wssUrl}`,
        keepAliveInterval: rtrProxy.sipKeepAliveInterval,
        keepAliveDebounce: rtrProxy.sipKeepAliveInterval
      },
      logBuiltinEnabled: rtrProxy.traceSip,
      gracefulShutdown: true,
      logConfiguration: false,
      displayName: AppConfigService.role
      // userAgentString: `SipJs--${LIBRARY_VERSION}`
    };


    const options: SimpleUserOptions = {
      userAgentOptions,
      reconnectionAttempts: Constants.MAX_ONREJECT_RETRY,
      reconnectionDelay: 15,
      delegate: this.createUserAgentDelegate(),
      aor,
      media: {
        // local: function(): SimpleUserMediaLocal { //dave serve solo per fare il "play" del video locale quando la call è attivo: session-manager private setupLocalMedia()
        //   return {video: (document.getElementById('localVideo') as HTMLVideoElement)}
        // },
        remote: function (s: Session): SimpleUserMediaRemote {

          return { video: selfThis.mediaService.getRemoteVideoRender(s.remoteIdentity.uri.user) }// 
          //(document.getElementById('remoteVideo') as HTMLVideoElement) }
        }
      },
      registererOptions: { logConfiguration: false, expires: rtrProxy.registerExpires, refreshFrequency: 95, extraHeaders: this.getDefaultExtraHeaders() }
    };
    this.ua = new SimpleUser(rtrProxy.wssUrl, options);

    this.connect();

  }

  disconnecting = false
  disconnect() {
    if (!this.ua || this.disconnecting) return;
    console.trace(`sipService: disconnect`)

    this.disconnecting = true;
    this.ua?.disconnect()
      .catch(() => this.disconnecting = false)
      .finally(() => this.disconnecting = false);

  }

  // solo al "primo"
  sendMessage(body: string, to: string, first: boolean) {
    const rtrProxy = this.config.rtrProxy;
    const toAor = `sip:${to}@${rtrProxy.domain}`;
    const options: MessagerOptions = {
      extraHeaders: [...this.getDefaultExtraHeaders(), `SM-StoreMessage: ${first}`]
    }
    this.ua?.message(toAor, body, 'text/plain', options)

  }

  checkPartecipantToCall() {
    if (this.config.isReceiver && !this.config.isSuper) return;
    // if (!this.onCallPipe.transform( this.config.AS?.phoneStatus, true) && // non sono in call
    //   !this.config.operatorsStatus.find(z => this.onCallPipe.transform(z.phoneStatus, true)) //e non c'è operatore in call
    // ) {
    //   return;
    // }
    if (!this.config.iamOnCall) return;
    if (!this.isRegistered) return;
    if (this.stoppingAllSessions) return

    // Debounce: Non controllare se c'è stato un hangup negli ultimi 3 secondi
    // Questo dà tempo al phoneStatus di sincronizzarsi via SignalR dopo la chiusura
    const timeSinceLastHangup = Date.now() - this.lastHangupTime;
    if (timeSinceLastHangup < 3000) {
      console.log(`sipService checkPartecipantToCall - skipping: recent hangup ${timeSinceLastHangup}ms ago, waiting for state sync`);
      return;
    }

    // Recupera SOLO chiamate verso chi è ANCORA in call secondo lo stato del server
    // Questo previene di richiamare chi ha chiuso volontariamente
    const operatorsToCall = this.config.operatorsStatus
      .filter(z => !this.ua?.onCallWith(z.partecipantId))
      .filter(z => this.config.onCallPipe.transform(z.phoneStatus)); // Verifica che sia ancora in call

    const othersToCall = this.config.otherPartecipantsOnCall // già filtrato per phoneStatus in otherPartecipantsOnCall
      .filter(z => !this.ua?.onCallWith(z.partecipantId));

    const toCall = [...operatorsToCall, ...othersToCall];

    if (toCall.length) {
      console.log(`sipService checkPartecipantToCall => ${toCall.length}`)
      this.callPartecipants(toCall, true);
    }
  }

  handleCalls() {
    if (this.stoppingAllSessions) return;

    //se sono un receiver.. enable/disable media [non faccio chiamate se mi sposto in video]
    if (this.config.isReceiver && !this.config.isSuper) {
      this.enableMedia();
      return;
    }

    const appState = this.config.AS;
    // tutti gli altri faranno una chiamata 
    // se video/audio abilitao (al momento solo cittatino)
    const videoOrAudioEnabled = appState?.videoEnabled || appState?.microphoneEnabled;
    // oppure se sono on-site-operator
    const onSiteOpInCall = this.config.isOnSiteOperator;
    // oppure se sono guest e ci sono altri in chiamata
    const guestWithOthersInCall = this.config.isGuest && this.config.otherPartecipantsOnCall.length > 0;

    const wantsToCall = videoOrAudioEnabled || onSiteOpInCall || guestWithOthersInCall;

    if (!this.isInCall() && !wantsToCall) { //se sono già in call e non devo chiamare
      console.log(`sipService handleCalls - skipping: not in call and media disabled`);
      return;
    }

    //startCalls for others
    // Filtra operatori non ancora registrati su SIP per evitare 404 Not Found:
    // partecipantAdded arriva via SignalR PRIMA che l'operatore completi REGISTER sul proxy SIP.
    // L'effect operatorSipRegistered in app.effects.ts ri-triggererà handleCalls quando isRegistered diventa true.
    const registeredOperators = this.config.operatorsStatus.filter(z => z.isRegistered);
    const skippedOperators = this.config.operatorsStatus.length - registeredOperators.length;
    const toCall = [...registeredOperators, ...this.config.otherPartecipantsOnCall, ...this.config.anyOtherPaticipantOnVideoSection];
    if (skippedOperators > 0) {
      console.log(`sipService handleCalls => ${toCall.length} (skipped ${skippedOperators} unregistered operators)`);
    } else {
      console.log(`sipService handleCalls => ${toCall.length}`, this.config.anyOtherPaticipantOnVideoSection)
    }
    this.callPartecipants(toCall);
  }

  private partecipantsToCall: PartecipantStatus[] = [];
  private callingPartecipants = false;
  private lastCallingPartecipants: number = 0;
  private readonly CALL_INTERVAL = 1000;
  private callPartecipants(partecipants: PartecipantStatus[], automatic = false) {

    if (partecipants.length == 0 && this.partecipantsToCall.length == 0) {
      this.callingPartecipants = false;
      return;
    }
    const now = Date.now();
    if (this.callingPartecipants && (now - this.lastCallingPartecipants) < 17 * 1000 && automatic) {
      console.log(`sipService - already callingPartecipants`)
      return;
    }
    this.callingPartecipants = true;
    this.lastCallingPartecipants = now;

    this.partecipantsToCall = [...this.partecipantsToCall];
    partecipants.forEach(p => {
      if (!this.partecipantsToCall.find(z => z.partecipantId == p.partecipantId)) {
        this.partecipantsToCall.push(p);
      }
    });


    //this.dequeuePartecipantsToCall();
    this.partecipantsToCall.forEach((p, idx) => { //scodo tutti insieme

      setTimeout(() => {
        this.dequeuePartecipantsToCall();
      }, this.CALL_INTERVAL * idx)
    })


    // const appState = this.config.AS!;
    // partecipants.forEach( (p, idx) => {
    //   setTimeout(()=>{
    //     this.startCall({ to: p.partecipantId, toType: p.partecipantRole, enableAudio: appState.microphoneEnabled, enableVideo: appState.videoEnabled})
    //   }, this.CALL_INTERVAL*idx)
    // });
    // const promises = partecipants.map( (p: PartecipantStatus) => { 
    //   return this.startCall({ to: p.partecipantId, toType: p.partecipantRole, enableAudio: appState.microphoneEnabled, enableVideo: appState.videoEnabled}).
    //     then(()=>{
    //       console.log(`sipService startedCall to [${ p.partecipantRole}] ${p.partecipantId}`);
    //   })
    // })

    // return Promise.all(promises).then();


  }
  private dequeuePartecipantsToCall() {
    if (this.partecipantsToCall.length == 0) {
      this.callingPartecipants = false;
      return;
    }
    const p = this.partecipantsToCall.shift();
    if (!p) {
      if (this.partecipantsToCall.length > 0) {
        this.dequeuePartecipantsToCall();
        return;
      }
      this.callingPartecipants = false;
      return;
    }
    const appState = this.config.AS!;
    this.startCall({ to: p.partecipantId, toType: p.partecipantRole, enableAudio: appState.microphoneEnabled, enableVideo: appState.videoEnabled })
  }

  private startCall(opts: { to: string, toType: PartecipantRoles, enableVideo: boolean, enableAudio: boolean }): Promise<void> {
    if (this.ua?.onCallWith(opts.to)) {//} && !opts.force) { //sessione esiste già, sistemiamo i flussi   //dave-mvideo!!!
      console.log(`sipService - enablingMedia`)
      return this.enableMedia()//{ audio: opts.enableAudio, video: opts.enableVideo });

    }

    if (this.config.isReceiver && !this.config.isSuper) return Promise.resolve(); // full receiver don't start calls!!
    console.log(`sipService startCall to [${opts.toType}] ${opts.to}`);

    const myoptions: SessionInviteOptions = {
      requestOptions: {
        extraHeaders: this.getDefaultExtraHeaders(),
      },
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: true
        }
      },

    };
    const options = { ...myoptions }; //, ...otherOptions };
    const inviterOptions: InviterOptions = {
      extraHeaders: this.getDefaultExtraHeaders(),
      params: { toDisplayName: opts.toType }
    };

    console.log(`sipService - callRequest STARTING to [${opts.toType}] ${opts.to}`)
    return this.ua?.call(this.createAor(opts.to), inviterOptions, myoptions) || Promise.resolve()

  }

  endAllCalls() {
    if (!this.isInCall()) return;
    this.stoppingAllSessions = true;

    // Svuota la coda di partecipanti da chiamare per prevenire chiamate schedulate
    // che verrebbero eseguite dopo la chiusura delle sessioni correnti
    this.partecipantsToCall = [];
    this.callingPartecipants = false;

    console.log(`sipService - endAllCalls - this.stoppingAllSessions ${this.stoppingAllSessions}`);
    this.ua?.hangupAll();
  }

  endCall(user: string) {
    if (!this.isInCall()) return;
    this.ua?.hangup(user);
  }

  private enableMedia(): Promise<void> {//opts: MediaEnable) {
    if (!this.isInCall()) return Promise.resolve();

    if (!this.mediaService.isCurrentMediaChanged()) {
      console.log(`sipService - already enableMedia . return`);
      return Promise.resolve();
    }


    console.log(`sipService - enableMedia`);
    const self = this;

    return this.mediaService.setupLocalMediaForCall()//opts)
      .then((stream: MediaStream) => {
        if (stream.getTracks().length) {
          self.replaceStream(stream);
        }
        self.store.dispatch(phoneActions.phoneChangeStatus({ status: PhoneStatusEnum.TALKING }));
      })
      .catch((e) => {
        FELoggerService.error(`sipService - enableMedia  ERROR`, LogSource.MediaAudio, e)
      });
  }

  public switchCamera(useSelectedId = false) {
    if (!this.isInCall()) return;
    console.log(`sipService - setupLocalMediaForCall - switchCamera`);
    const self = this;

    //stop current sending Streams (no open concurrent camera on android)
    this.stopSendingStreams()

    this.mediaService.switchCameraForCall(useSelectedId).then((stream: MediaStream) => {
      self.replaceStream(stream);
    })
      .catch((e) => {
        FELoggerService.error(`sipService - switchCamera ERROR`, LogSource.MediaAudio, e)
      });
  }
  public changeMediaDevice() {
    if (!this.isInCall()) return;
    console.log(`sipService - setupLocalMediaForCall - changeMediaDevice`);
    const self = this;

    //stop current sending Streams (no open concurrent camera on android)
    this.stopSendingStreams()

    this.mediaService.setupLocalMediaForCall(true, true).then((stream: MediaStream) => {
      self.replaceStream(stream);
    })
      .catch((e) => {
        FELoggerService.error(`sipService - switchCamera ERROR`, LogSource.MediaAudio, e)
      });
  }
  // public videoEditStream(start: boolean) {
  //   if (start && this.mediaService.videoEditStream) {
  //     this.replaceStream(this.mediaService.videoEditStream);
  //   } else {
  //     this.enableMedia();
  //   }
  // }

  private replaceStream(stream: MediaStream) {
    if (!this.isInCall()) return;
    const videoTrack = stream.getVideoTracks()[0];
    const audioTrack = stream.getAudioTracks()[0];
    this.ua?.sessions.forEach(session => {
      // const sdh = this.ua?.currentSession?.sessionDescriptionHandler;
      const sdh = session.sessionDescriptionHandler;
      if (sdh && sdh instanceof SessionDescriptionHandler) {
        if (sdh.peerConnection) {
          sdh.peerConnection.getSenders().forEach(s => {
            if (s.track && s.track.kind == 'video' && videoTrack.kind == 'video' && s.track?.id != videoTrack.id) {
              console.log(`sipService - replacing video track ${s.track?.id} whit ${videoTrack.id}`);
              s.replaceTrack(videoTrack).then(() => {
                console.log(`sipService - video track replaced successfully on session id:${session.id}`, sdh.peerConnection?.getSenders());//, Framework.ReinviteDirection);
              });
            }
            if (s.track && s.track.kind == 'audio' && audioTrack.kind == 'audio' && s.track?.id != audioTrack.id) {
              console.log(`sipService - replacing audio track ${s.track?.id} whit ${audioTrack.id}`);
              s.replaceTrack(audioTrack).then(() => {
                console.log(`sipService - audio track replaced successfully on session id:${session.id}`, sdh.peerConnection?.getSenders());//, Framework.ReinviteDirection);
              });
            }
          });
        }
      }
    })
  }

  private stopSendingStreams() {
    if (!this.isInCall()) return;
    this.ua?.stopSendingStreams();
  }

  private createUserAgentDelegate(): SimpleUserDelegate {
    const selfThis = this;
    const delegate: SimpleUserDelegate = {
      onServerConnect: () => {
        // [PERF-LOAD-14] SIP Connected
        const perfNow = performance.now();
        const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
        console.log(`[PERF-LOAD-14] SIP Connected | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

        console.log('sipService - onServerConnect')
        selfThis.store.dispatch(appActions.wssSipConnected());
      },

      onServerDisconnect: (error: Error) => {
        console.log(`sipService - onServerDisconnect ${error?.name} - ${error?.message}`)

        // ✅ PRIORITÀ: Controllare prima il suspended mode
        // Se in suspended mode, non mostrare errori anche se maxAttempts raggiunto
        // L'utente potrebbe ancora selezionare il file e riconnettersi
        if (selfThis.config.AS?.isSuspended) {
          console.log('SIP disconnected during suspended mode (maxAttempts: ' + (error?.name == 'maxAttempts') + ') - will retry on resume');
          return;
        }

        // Se NON in suspended mode e maxAttempts raggiunto, mostra errore
        if (error && error.name == 'maxAttempts') {
          const msg = `${I18Service.get('Error.Signalr')}\n${I18Service.get('SA.UNAVAILABLEGLBODY')}`
          selfThis.store.dispatch(appActions.showErrorPage({ message: msg }))
          return;
        }

        this.isRegistered = false;
        selfThis.store.dispatch(appActions.wssSipRegistered({ is: false }));

      },

      onCallReceived: async (invitation: Invitation) => {

        //blocca una eventuale seconda chiamata se già presente
        const from = invitation?.remoteIdentity?.uri?.user || '';
        const displayName = invitation?.remoteIdentity.displayName || 'none'
        const sessionCount = this.ua?.sessionCount(from) || 0;
        if (sessionCount > 1) {
          console.log(`sipService - onCallReceived - refuse because already on call with ${from}`);
          await this.ua?.decline(invitation);
          return;
        }

        console.log(`sipService - onCallReceived - Incoming Call from [${displayName}] ${from}!`);

        const opts: InvitationAcceptOptions = {
          extraHeaders: this.getDefaultExtraHeaders()
        }
        await this.ua?.answer(opts);
      },
      onCallCreated: (session: Session) => { // sia in che out
        this.logSessionEvent(session, 'onCallCreated to');
        selfThis.store.dispatch(phoneActions.phoneChangeStatus({ status: PhoneStatusEnum.CALLING }));
      },

      onCallAnswered: async (session: Session) => { // sia in che out
        this.logSessionEvent(session, 'onCallAnswered to');
        selfThis.store.dispatch(phoneActions.phoneChangeStatus({ status: PhoneStatusEnum.TALKING }));
        this.dequeuePartecipantsToCall();

        await selfThis.initializeVADForSession(session);
        // selfThis.statsService.startMonitoring(session);
      },

      onCallHangup: (session: Session) => {
        this.logSessionEvent(session, `onCallHangup remaining sessions: ${this.ua?.sessions.length} to`);

        // Aggiorna timestamp per debounce in checkPartecipantToCall
        this.lastHangupTime = Date.now();

        const remoteId = session.remoteIdentity.uri.user;
        if (remoteId) {
          selfThis.vadService.removeVAD(remoteId);
        }
        this.statsService.stopMonitoring(session.id);
        if (!this.ua?.sessions.length) {
          selfThis.store.dispatch(phoneActions.phoneChangeStatus({ status: PhoneStatusEnum.IDLE }));
          this.mediaService.stopLocalStreams();
          setTimeout(() => { // ritardo lo "sblocco"
            this.stoppingAllSessions = false;
            console.log(`sipService ********* SBLOCCO endAllSessions iamAnCall ${this.config.iamOnCall} -this.stoppingAllSessions:${this.stoppingAllSessions}`)

          }, 1500)
        } else {
          //proviamo a riattiare il remoteStream bloccato all'hangup
          // this.mediaService.playRemoteStream();
        }
        if (!this.stoppingAllSessions) {
          const timeout = this.config.isReceiver ? 1000 : 1500;
          setTimeout(() => { // ritardo lo "sblocco"
            this.mediaService.setZoomOnTalking();
          }, timeout)
        }
      },
      onMessageReceived: (from: URI, message: string) => {
        console.log('sipService - onMessageReceived');
        selfThis.store.dispatch(appActions.recvTextMessage({ body: message, from: from.user || from.toString(), rtpCallId: undefined }))
      },
      onRegistered: () => {
        // [PERF-LOAD-15] SIP Registered
        const perfNow = performance.now();
        const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
        console.log(`[PERF-LOAD-15] SIP Registered | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

        console.log('sipService - onRegistered')
        if (selfThis.tryRegister > 1) {
          selfThis.store.dispatch(appActions.loadingMessage({ message: '' }));
          selfThis.tryRegister = 1;
        }
        this.isRegistered = true;
        selfThis.store.dispatch(appActions.wssSipRegistered({ is: true }));
      },
      onUnregistered: () => {
        console.log('sipService - onUnregistered');
        this.isRegistered = false;
        selfThis.store.dispatch(appActions.wssSipRegistered({ is: false }));
      },
      onAttemptReconnection: (n, m) => {
        // Durante suspended mode, non mostrare i messaggi di tentativo riconnessione
        // per evitare di bloccare visivamente l'UI durante la selezione del file
        if (selfThis.config.AS?.isSuspended) {
          console.log(`SIP reconnection attempt ${n}/${m} during suspended mode - hiding message`);
          return;
        }

        // IMPORTANTE: Se la connessione è già attiva, non mostrare il messaggio
        // Questo previene che timer schedulati prima della riconnessione manuale
        // mostrino messaggi dopo che la connessione è già riuscita
        if (this.ua && this.ua.isConnected()) {
          console.log(`SIP reconnection attempt ${n}/${m} - skipping message, already connected`);
          return;
        }
        
        // NotificationService.show(I18Service.get('SNACKBAR.RECONNECTIONATTEMPT', {current: n, max: m}));
        const message = I18Service.get('SNACKBAR.RECONNECTIONATTEMPT', { current: n, max: m });
        FELoggerService.log(message, LogSource.PhoneEvents)
        selfThis.store.dispatch(appActions.loadingMessage({ message }));
      }
    };
    return delegate;
  }

  private connect() {
    if (!this.ua) return;
    console.log(`sip wss connecting`)
    this.ua.connect().
      then(() => {
        console.log(`sip wss connected`)
        // this.store.dispatch(appActions.wssSipConnected());
        this.register()
      }).
      catch((e) => {

        if (this.config.localDev) { //TODO HACK!! solo in localdev
          this.store.dispatch(appActions.wssSipRegistered({ is: true })); return;
        }

        FELoggerService.error(`sip wss connecting`, LogSource.SIPEvent, e);
        const msg = `${I18Service.get('Error.Signalr')}\n${I18Service.get('SA.UNAVAILABLEGLBODY')}`
        this.store.dispatch(appActions.showErrorPage({ message: msg }))
      })
  }

  private tryRegister = 1;
  private retryTimer: any;
  private register() {
    if (!this.ua) return;
    if (this.retryTimer) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    console.log(`sipService register`)
    const selfThis = this;
    const regOptions: RegistererRegisterOptions = {
      requestDelegate: {
        onReject(response) {
          FELoggerService.error(`register rejected isReceiver:${selfThis.config.isReceiver}`, LogSource.SIPEvent, response)
          if (response.message.statusCode == 408) {
            if (!selfThis.retryTimer) {

              if (selfThis.tryRegister > Constants.MAX_ONREJECT_RETRY) {
                selfThis.store.dispatch(appActions.loadingMessage({ message: '' }));
                const msg = `${I18Service.get('Error.Signalr')}\n${I18Service.get('SA.UNAVAILABLEGLBODY')}`
                selfThis.store.dispatch(appActions.showErrorPage({ message: msg }))
                return;
              }

              selfThis.retryTimer = setTimeout(() => selfThis.register(), 5000);
              selfThis.store.dispatch(appActions.loadingMessage(
                {
                  message: I18Service.get('SNACKBAR.RECONNECTIONATTEMPT', { current: selfThis.tryRegister, max: Constants.MAX_ONREJECT_RETRY }
                  )
                }));
              selfThis.tryRegister++;
            }
            return;
          }
          if (selfThis.config.isReceiver == false) {
            const msg = `${I18Service.get('Error.Signalr')}\n${I18Service.get('POPUP.SESSIONEXPIREDTITLE')}`
            selfThis.store.dispatch(appActions.showErrorPage({ message: msg, showReload: false }))
          }
        },
      }
      // requestOptions: {
      //   extraHeaders: this.getDefaultExtraHeaders()
      // }
    }
    this.ua.register(regOptions).
      then(() => {
        // console.log(`sip registered`);

        // this.store.dispatch(appActions.wssSipRegistered({is:true}));
        // this.store.dispatch(phoneActions.phoneChangeStatus({status: PhoneStatusEnum.IDLE}));
      }).
      catch((e) => { FELoggerService.error(`sip registered`, LogSource.SIPEvent, e); })

  }

  private getDefaultExtraHeaders(): string[] {
    return [
      `SM-PartecipantId: ${this.config.partecipantId}`,
      `SM-ClientType: ${AppConfigService.role}`,
      `SM-SessionId: ${this.config.sessionId}`
    ];
  }

  private createAor(user: string | undefined): string {
    if (!user) {
      throw new Error(`createAor problem: user is null`);
    }
    const rtrProxy = this.config.rtrProxy;
    return `sip:${user}@${rtrProxy.domain}`;
  }


  private isInCall(): boolean {
    // return !!this.ua?. currentSession;
    return !!this.ua && this.ua.sessions.length > 0
  }

  private logSessionEvent(session: Session, msg: string) {
    const who = `[${session?.remoteIdentity?.displayName}] ${session?.remoteIdentity?.uri?.user}`;
    FELoggerService.log(`${msg} ${who}`, LogSource.PhoneEvents);
  }


  public showCurrentSessions() {
    const who = `${AppConfigService.role} - ${this.config.partecipantId}`;
    const obj = this.ua?.showCurrentSessions(who);
    FELoggerService.log(`show current session for ${who}`, LogSource.Default, { who, list: obj });
    NotificationService.show(`sessioni correnti inviate`)
  }

  private async initializeVADForSession(session: Session): Promise<void> {
    try {
      const remoteId = session.remoteIdentity.uri.user;
      if (!remoteId) return;
      

      const remoteStream = this.ua?.gerRemoteMediaStream(session);
      if (!remoteStream) {
        console.warn(`VAD non inizializzato: stream remoto non disponibile per sessione ${remoteId}`);
        return;
      }

      // Ottieni lo stream remoto dalla sessione
      if (remoteStream && remoteStream.getAudioTracks().length > 0) {
        await this.vadService.initializeVAD(remoteId, remoteStream);
        console.log(`VAD inizializzato per sessione remota: ${remoteId}`);
      }
      
      // Inizializza anche per lo stream locale se non già fatto
      // await this.initializeLocalVAD();
    } catch (error) {
      console.error('Errore inizializzazione VAD per sessione:', error);
    }
  }
  // /**
  //  * Inizializza VAD per lo stream locale (una sola volta)
  //  */
  // private async initializeLocalVAD(): Promise<void> {
  //   if (this.vadService.hasVAD(this.config.partecipantId)) {
  //     return; // Già inizializzato
  //   }
    
  //   const localStream = await this.mediaService.getCurrentLocalStream();
  //   if (localStream && localStream.getAudioTracks().length > 0) {
  //     await this.vadService.initializeVAD(this.config.partecipantId, localStream);
  //     console.log(`VAD inizializzato per il proprio stream audio`);
  //   }
  // }

}